import { and, eq, sql } from 'drizzle-orm'

import type { Database } from '@package/drizzle/client'
import type { InsertNotification, NotificationCategory } from '@package/drizzle/schema'
import { Notification, NotificationEvent } from '@package/drizzle/schema'

import type { DatabaseOrTransaction, NotificationContext, NotificationSnapshot } from './types'
import { canNotify } from './policy'

export interface EmitInput {
  type: string
  category: NotificationCategory
  /** Null for cron-generated events. */
  actorId: string | null
  recipientIds: string[]
  /**
   * `type` is a plain string, matching the `text` column behind it: notification kinds outside the
   * share family (`HOME_CONNECT_REQUEST`, `COURSE`, …) point at entities that were never share
   * entities, and requiring a `ShareEntityType` here would force an enum migration per kind.
   * Resolvers already default unknown values to `{ kind: 'none' }`.
   */
  entity?: { type: string; id: string }
  snapshot?: NotificationSnapshot
  context?: NotificationContext
  shareId?: string
  /** When set, a re-run with the same key is a no-op instead of a duplicate. */
  dedupeKey?: string
  /** Create an ACTOR row for the actor so they see their own send in the feed. */
  notifyActor?: boolean
  /** For roll-up events ("5 siswa belum..."), the number of subjects counted. */
  subjectCount?: number
}

export interface EmitResult {
  eventId: string | null
  recipientCount: number
  /** True when a dedupeKey collision meant nothing was written. */
  deduped: boolean
}

/**
 * Creates one event plus one notification row per recipient, and optionally one
 * ACTOR row for the sender. Synchronous and transactional: the in-app feed must
 * not depend on the job queue.
 *
 * Channel delivery (email, push) is enqueued separately in phase 3.
 *
 * `db` accepts either a pooled `Database` or a transaction handle, so callers that
 * already hold a `tx` (e.g. a router mutation that writes the shared entity and emits
 * the notification atomically) can pass it straight through.
 */
export async function emit(db: DatabaseOrTransaction, input: EmitInput): Promise<EmitResult> {
  const recipientIds = [...new Set(input.recipientIds)]

  /**
   * An actor receipt is reason enough to write the event even with nobody to notify. Sambung
   * Sekolah-Rumah sends to an email address that may have no account yet: the parent gets the mail
   * and no card, but the teacher must still see "permohonan terkirim" in their own feed.
   */
  const wantsActorRow = Boolean(input.notifyActor && input.actorId)

  if (recipientIds.length === 0 && !wantsActorRow) {
    return { eventId: null, recipientCount: 0, deduped: false }
  }

  if (input.actorId && recipientIds.length > 0) {
    const allowed = await canNotify(db, {
      actorId: input.actorId,
      recipientIds,
      type: input.type,
    })
    if (!allowed) {
      throw new Error(`canNotify rejected ${input.type} from ${input.actorId}`)
    }
  }

  return db.transaction(async (tx) => {
    const inserted = await tx
      .insert(NotificationEvent)
      .values({
        type: input.type,
        actorId: input.actorId,
        entityType: input.entity?.type ?? null,
        entityId: input.entity?.id ?? null,
        shareId: input.shareId ?? null,
        snapshot: input.snapshot ?? {},
        context: input.context ?? {},
        dedupeKey: input.dedupeKey ?? null,
        recipientCount: recipientIds.length,
        subjectCount: input.subjectCount ?? 0,
      })
      .onConflictDoNothing({ target: NotificationEvent.dedupeKey })
      .returning()

    const event = inserted[0]
    if (!event) {
      // dedupeKey collision — a prior run already emitted this.
      return { eventId: null, recipientCount: 0, deduped: true }
    }

    // Explicitly typed: without this annotation the array's element type is
    // inferred from the `RECIPIENT` literal below, and pushing an `ACTOR` row
    // afterwards fails to compile (literal-widening trap).
    const rows: InsertNotification[] = recipientIds.map((userId) => ({
      eventId: event.id,
      userId,
      audience: 'RECIPIENT',
      category: input.category,
    }))

    if (input.notifyActor && input.actorId) {
      rows.push({
        eventId: event.id,
        userId: input.actorId,
        audience: 'ACTOR',
        category: input.category,
      })
    }

    await tx.insert(Notification).values(rows).onConflictDoNothing()

    return { eventId: event.id, recipientCount: recipientIds.length, deduped: false }
  })
}

/** The thing a roll-up counts. One per call; repeats of the same `id` are free. */
export interface RollupSubject {
  /** Stable identity of the subject — a child's user id. Deduped against the group, not the actor. */
  id: string
}

export interface EmitRollupInput extends Omit<EmitInput, 'dedupeKey' | 'notifyActor' | 'subjectCount'> {
  /**
   * Required here, unlike on {@link emit}: the key IS the group. Every call sharing it lands on the
   * same card instead of minting another one, so the key doubles as the roll-up's bucket — scope it
   * to the audience and to whatever window the group should reset on.
   */
  dedupeKey: string
  subject: RollupSubject
}

export interface EmitRollupResult {
  eventId: string | null
  /** Distinct subjects in the group after this call. */
  subjectCount: number
  /** True when this call opened the group, false when it joined one already open. */
  created: boolean
  /** True when this subject was already in the group — nothing was written. */
  alreadyCounted: boolean
}

/**
 * Emit that accumulates instead of duplicating: the first call writes the event, later calls in the
 * same bucket fold their subject into it and re-open it for the audience.
 *
 * {@link emit} cannot do this. Its `dedupeKey` is a no-op guard — a collision writes nothing and
 * reports `deduped` — which is right for "the same event twice" and wrong for "another child is
 * asking too". Nineteen children asking a school for premium access should be one card that counts
 * to nineteen, not nineteen cards; that count has to be of DISTINCT children, so the group carries
 * the set it has already counted in `snapshot.studentIds`.
 *
 * No actor receipt: a roll-up card belongs to the audience, not to any one subject, so an ACTOR row
 * on it would show child A a card about child B. Callers confirm the send themselves.
 */
export async function emitRollup(db: DatabaseOrTransaction, input: EmitRollupInput): Promise<EmitRollupResult> {
  const recipientIds = [...new Set(input.recipientIds)]

  /**
   * No empty-audience escape hatch either, for the same reason: counting a child into a group that
   * nobody can open would report success for a request that reached no one. Callers resolve the
   * audience first and treat an empty one as an error the child can act on.
   */
  if (recipientIds.length === 0) {
    return { eventId: null, subjectCount: 0, created: false, alreadyCounted: false }
  }

  if (input.actorId) {
    const allowed = await canNotify(db, {
      actorId: input.actorId,
      recipientIds,
      type: input.type,
    })
    if (!allowed) {
      throw new Error(`canNotify rejected ${input.type} from ${input.actorId}`)
    }
  }

  return db.transaction(async (tx) => {
    const recipientRows = (eventId: string): InsertNotification[] =>
      recipientIds.map((userId) => ({
        eventId,
        userId,
        audience: 'RECIPIENT',
        category: input.category,
      }))

    const inserted = await tx
      .insert(NotificationEvent)
      .values({
        type: input.type,
        actorId: input.actorId,
        entityType: input.entity?.type ?? null,
        entityId: input.entity?.id ?? null,
        snapshot: { ...input.snapshot, studentIds: [input.subject.id] },
        context: input.context ?? {},
        dedupeKey: input.dedupeKey,
        recipientCount: recipientIds.length,
        subjectCount: 1,
      })
      .onConflictDoNothing({ target: NotificationEvent.dedupeKey })
      .returning()

    const opened = inserted[0]
    if (opened) {
      await tx.insert(Notification).values(recipientRows(opened.id)).onConflictDoNothing()

      return { eventId: opened.id, subjectCount: 1, created: true, alreadyCounted: false }
    }

    /**
     * The group is already open. Lock it before the read-modify-write on `studentIds`: two children
     * clicking in the same second would otherwise both read the old array and the second write
     * would silently drop the first one's id, undercounting the group forever.
     */
    const [existing] = await tx
      .select()
      .from(NotificationEvent)
      .where(eq(NotificationEvent.dedupeKey, input.dedupeKey))
      .limit(1)
      .for('update')

    // Only reachable if the group was deleted between the two statements above.
    if (!existing) {
      return { eventId: null, subjectCount: 0, created: false, alreadyCounted: false }
    }

    const counted = existing.snapshot.studentIds ?? []
    if (counted.includes(input.subject.id)) {
      return {
        eventId: existing.id,
        subjectCount: existing.subjectCount,
        created: false,
        alreadyCounted: true,
      }
    }

    const studentIds = [...counted, input.subject.id]

    await tx
      .update(NotificationEvent)
      .set({
        snapshot: { ...existing.snapshot, studentIds },
        subjectCount: studentIds.length,
        // The audience can grow between bumps — an admin who joined the school this morning.
        recipientCount: Math.max(existing.recipientCount, recipientIds.length),
        lastActivityAt: new Date(),
        // The sentence on the card just changed, so nobody has read THIS one. Reset in step with
        // the per-recipient `readAt` below, or the sender-side progress and the feed disagree.
        readCount: 0,
      })
      .where(eq(NotificationEvent.id, existing.id))

    await tx.insert(Notification).values(recipientRows(existing.id)).onConflictDoNothing()

    /**
     * Re-open the card. Without this the nineteenth request lands silently on an admin who already
     * read the first one — exactly the miss the roll-up exists to prevent. `seenAt` goes too so the
     * ACTOR-side "new activity" rule in `isUnread` cannot hold a stale seen timestamp.
     */
    await tx
      .update(Notification)
      .set({ readAt: null, seenAt: null })
      .where(and(eq(Notification.eventId, existing.id), eq(Notification.audience, 'RECIPIENT')))

    return { eventId: existing.id, subjectCount: studentIds.length, created: false, alreadyCounted: false }
  })
}

/**
 * Marks a recipient row read and bumps the event counters that drive the
 * sender's "12 sudah membuka" progress. Both happen in one transaction so the
 * two views can never disagree.
 */
export async function markNotificationRead(db: Database, notificationId: string, userId: string): Promise<void> {
  await db.transaction(async (tx) => {
    const updated = await tx
      .update(Notification)
      .set({ readAt: new Date() })
      .where(
        sql`${Notification.id} = ${notificationId} AND ${Notification.userId} = ${userId} AND ${Notification.readAt} IS NULL`,
      )
      .returning()

    const row = updated[0]
    if (!row || row.audience !== 'RECIPIENT') return

    await tx
      .update(NotificationEvent)
      .set({
        readCount: sql`${NotificationEvent.readCount} + 1`,
        lastActivityAt: new Date(),
      })
      .where(eq(NotificationEvent.id, row.eventId))
  })
}
